import { NotificationDot } from '@src/components/NotificationBadge/NotificationBadge';
import { getPortalRoot } from '@src/components/portalRoot';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMeasureCssVariable } from '@src/components/layout/useMeasureCssVariable';
import {
  MOBILE_HEADER_MQ,
  useMatchesMedia,
  useMobileHeaderRecede,
} from '@src/components/layout/useMobileHeaderRecede';
import { Bar } from '@src/components/Bar/Bar';
import { Cluster } from '@src/components/Cluster/Cluster';
import {
  Container,
  type ContainerGutter,
  type ContainerWidth,
} from '@src/components/Container/Container';
import { Drawer } from '@src/components/Drawer/Drawer';
import { Hamburger } from '@src/components/Hamburger/Hamburger';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import './AppHeader.css';

export interface AppHeaderProps {
  /** Brand, a title, a kicker — stays visible on a narrow screen. */
  children: ReactNode;
  /** Shown on the right on a wide header; move into the left drawer when narrow. */
  actions?: ReactNode;
  /** Keep actions out of the bar — they only appear in the hamburger drawer. */
  drawerOnly?: boolean;
  /** Stay on the bar even when actions move into the drawer. */
  tools?: ReactNode;
  width?: ContainerWidth;
  gutter?: ContainerGutter;
  /** Stick to the viewport. Off for a nested specimen. */
  sticky?: boolean;
  menuLabel?: string;
  menuUnread?: boolean;
  menuTitle?: string;
  closeLabel?: string;
  className?: string;
  testId?: string;
}

/** Sticky top chrome. On a narrow header the hamburger opens a left drawer. */
export function AppHeader({
  children,
  actions,
  drawerOnly = false,
  tools,
  width = 'content',
  gutter = 'default',
  sticky = true,
  menuLabel = 'Open menu',
  menuUnread = false,
  menuTitle = 'Menu',
  closeLabel = 'Close menu',
  className,
  testId,
}: AppHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const mobileFloating = Boolean(sticky && actions);
  const narrowViewport = useMatchesMedia(MOBILE_HEADER_MQ);
  const mobilePillOnly = mobileFloating && narrowViewport;
  const recede = useMobileHeaderRecede(mobilePillOnly, menuOpen);
  const measureHeader = sticky && !mobilePillOnly;
  useMeasureCssVariable(headerRef, '--size-app-header-measured', measureHeader);

  useLayoutEffect(() => {
    if (!mobilePillOnly) {
      return;
    }
    document.documentElement.style.setProperty('--size-app-header-measured', '0px');
  }, [mobilePillOnly]);

  const menuDrawer = actions ? (
    <Drawer
      className="app-header-menu"
      open={menuOpen}
      onClose={() => setMenuOpen(false)}
      side="start"
      title={menuTitle}
      closeLabel={closeLabel}
      testId={testId ? `${testId}-drawer` : undefined}
    >
      <div
        className="app-header__menu"
        onClickCapture={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest('a[href], button:not(:disabled)')
          ) {
            // Keep the link mounted until its router click handler has run.
            // Closing during capture can turn this into a full document navigation.
            window.setTimeout(() => setMenuOpen(false), 0);
          }
        }}
      >
        {actions}
      </div>
    </Drawer>
  ) : null;

  const portalTarget =
    typeof document !== 'undefined' &&
    document.body &&
    'nodeType' in document.body &&
    document.body.nodeType === 1
      ? getPortalRoot()
      : null;

  return (
    <header
      ref={headerRef}
      className={classes(
        'app-header',
        sticky && 'app-header--sticky',
        mobileFloating && 'app-header--mobile-pill',
        recede && 'app-header--recede',
        drawerOnly && 'app-header--drawer-only',
        className,
      )}
      {...testIdAttr(testId)}
    >
      <Container width={width} gutter={gutter} pad="header">
        <Bar wrap={false} align="center" className="app-header__bar">
          <Cluster gap={4} align="center" wrap={false} className="app-header__start">
            {actions && (
              <span className="app-header__hamburger">
                <Hamburger
                  open={menuOpen}
                  onClick={() => setMenuOpen(true)}
                  label={menuLabel}
                  testId={testId ? `${testId}-menu` : undefined}
                />
                {menuUnread && <NotificationDot className="app-header__notification-dot" />}
              </span>
            )}
            {children}
          </Cluster>
          {(actions || tools) && (
            <Cluster gap={4} wrap={false} className="app-header__end">
              {actions && (
                <Cluster gap={4} className="app-header__actions">
                  {actions}
                </Cluster>
              )}
              {tools ? (
                <Cluster gap={4} wrap={false} className="app-header__tools">
                  {tools}
                </Cluster>
              ) : null}
            </Cluster>
          )}
        </Bar>
      </Container>
      {menuDrawer && portalTarget ? createPortal(menuDrawer, portalTarget) : null}
    </header>
  );
}
